<template>
  <div 
    v-if="shouldShowAd" 
    :class="adContainerClass"
    :style="{ minHeight: minHeight }"
  >
    <!-- Web fallback (for development) -->
    <div 
      v-if="!isNative" 
      class="bg-gray-200 border-2 border-dashed border-gray-400 rounded-lg flex items-center justify-center text-gray-600"
      :style="{ height: minHeight }"
    >
      <div class="text-center">
        <p class="text-sm font-medium">{{ adType.toUpperCase() }} AD</p>
        <p class="text-xs mt-1">Mobile Preview</p>
      </div>
    </div>
    
    <!-- Native ad placeholder (actual ad shown by AdMob) -->
    <div 
      v-else-if="isNative && adLoaded"
      :id="`admob-${adType}-${adId}`"
      class="w-full"
    />
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, onUnmounted, watch } from 'vue';
import { Capacitor } from '@capacitor/core';
import { BannerAdPosition } from '@capacitor-community/admob';
import { admobService } from '../services/admobService';
import { useConsentStore } from '../stores/consentStore';

interface Props {
  adType: 'banner' | 'rectangle';
  position?: BannerAdPosition;
  minHeight?: string;
  class?: string;
}

const props = withDefaults(defineProps<Props>(), {
  position: BannerAdPosition.BOTTOM_CENTER,
  minHeight: '90px',
  class: ''
});

const consentStore = useConsentStore();
const adLoaded = ref(false);
const adId = ref(`ad-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`);

const isNative = computed(() => Capacitor.isNativePlatform());
const shouldShowAd = computed(() => {
  return consentStore.canUseAdvertising && admobService.isEnabled();
});

const adContainerClass = computed(() => {
  const baseClasses = 'w-full flex justify-center items-center';
  return `${baseClasses} ${props.class}`;
});

const loadAd = async () => {
  if (!shouldShowAd.value || !isNative.value) {
    return;
  }

  try {
    if (props.adType === 'banner' || props.adType === 'rectangle') {
      const success = await admobService.showBannerAd(props.position);
      adLoaded.value = success;
    }
  } catch (error) {
    console.error('Failed to load ad:', error);
    adLoaded.value = false;
  }
};

const hideAd = async () => {
  if (isNative.value && adLoaded.value) {
    try {
      await admobService.hideBannerAd();
      adLoaded.value = false;
    } catch (error) {
      console.error('Failed to hide ad:', error);
    }
  }
};

// Watch for consent changes
watch(shouldShowAd, (newValue) => {
  if (newValue) {
    loadAd();
  } else {
    hideAd();
  }
});

onMounted(async () => {
  if (shouldShowAd.value) {
    // Small delay to ensure AdMob is initialized
    setTimeout(loadAd, 500);
  }
});

onUnmounted(() => {
  hideAd();
});
</script>

<style scoped>
/* Component-specific styles */
</style>